// Plain-text lounge formatting commands. No HTML is interpreted here: the output
// is the existing text/segments JSON structure used by both game clients.
const WHITE = '#ffffff';
const cleanColor = (value, fallback = WHITE) => /^#[0-9a-f]{6}$/i.test(String(value || '')) ? String(value).toLowerCase() : fallback;
const cleanSize = (value, fallback = 16) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(12, Math.min(48, Math.round(n))) : fallback;
};
const cleanWeight = (value) => {
  const v = String(value ?? 'normal').toLowerCase();
  return ['bold','600','700'].includes(v) ? 'bold' : ['medium','500'].includes(v) ? '500' : 'normal';
};
const normalize = (style = {}) => ({
  fontSize: cleanSize(style.fontSize),
  fontWeight: cleanWeight(style.fontWeight),
  color: cleanColor(style.color),
  fontStyle: style.fontStyle === 'italic' ? 'italic' : 'normal',
  textDecoration: String(style.textDecoration || '').includes('underline') ? 'underline' : 'none',
});
const identical = (a, b) => ['fontSize','fontWeight','color','fontStyle','textDecoration'].every((key) => a[key] === b[key]);
const normalizedLink = (raw) => /^https?:\/\/\S+$/i.test(String(raw || '')) ? String(raw) : '';

/** Converts simple, nested [b]/[u]/[size=]/[color=] tags to safe text segments. */
export function parseLoungeMarkup(input, baseStyle = {}) {
  const raw = String(input ?? '');
  const segments = [];
  let style = normalize(baseStyle);
  let linkUrl = '';
  const stack = [];
  const append = (text) => {
    if (!text) return;
    const previous = segments[segments.length - 1];
    if (previous && previous.linkUrl === linkUrl && identical(previous.style, style)) previous.text += text;
    else segments.push({ text, style: { ...style }, linkUrl });
  };
  for (let index = 0; index < raw.length;) {
    // Escape any literal opening bracket/backslash with a leading backslash.
    if (raw[index] === '\\' && ['[', '\\'].includes(raw[index + 1])) {
      append(raw[index + 1]); index += 2; continue;
    }
    if (raw[index] === '[') {
      const end = raw.indexOf(']', index + 1);
      if (end >= 0 && end - index <= 2048) {
        const token = raw.slice(index + 1, end);
        if (/^br$/i.test(token)) { append('\n'); index = end + 1; continue; }
        const spaces = token.match(/^space=(\d{1,2})$/i);
        if (spaces && Number(spaces[1]) >= 1 && Number(spaces[1]) <= 20) {
          append(' '.repeat(Number(spaces[1]))); index = end + 1; continue;
        }
        const closing = token.match(/^\/(b|u|i|size|color|weight|underline|italic|link)$/i);
        if (closing) {
          const tag = closing[1].toLowerCase();
          const last = stack[stack.length - 1];
          if (last?.tag === tag) {
            stack.pop(); style = last.style; linkUrl = last.linkUrl;
            index = end + 1; continue;
          }
        }
        const opening = token.match(/^(b|u|i|size|color|weight|underline|italic|link)(?:=(.+))?$/i);
        if (opening && stack.length < 64) {
          const tag = opening[1].toLowerCase();
          const val = opening[2];
          let patch = null; let nextLink = linkUrl;
          if (tag === 'b' && val === undefined) patch = { fontWeight: 'bold' };
          else if (tag === 'u' && val === undefined) patch = { textDecoration: 'underline' };
          else if (tag === 'i' && val === undefined) patch = { fontStyle: 'italic' };
          else if (tag === 'size' && /^\d{2}$/.test(val || '') && Number(val) >= 12 && Number(val) <= 48) patch = { fontSize: Number(val) };
          else if (tag === 'color' && /^#[a-f\d]{6}$/i.test(val || '')) patch = { color: cleanColor(val) };
          else if (tag === 'weight' && /^(normal|bold|500)$/.test(val || '')) patch = { fontWeight: val };
          else if (tag === 'underline' && /^(on|off)$/.test(val || '')) patch = { textDecoration: val === 'on' ? 'underline' : 'none' };
          else if (tag === 'italic' && /^(on|off)$/.test(val || '')) patch = { fontStyle: val === 'on' ? 'italic' : 'normal' };
          else if (tag === 'link' && normalizedLink(val)) { patch = {}; nextLink = val; }
          if (patch !== null) {
            stack.push({ tag, style: { ...style }, linkUrl });
            style = { ...style, ...patch }; linkUrl = nextLink;
            index = end + 1; continue;
          }
        }
      }
    }
    append(raw[index]); index += 1;
  }
  return segments;
}

// Serialize existing rich-editor spans losslessly, including legacy links,
// so switching to the command editor doesn't discard previously styled text.
export function serializeLoungeMarkup(block) {
  const base = normalize(block?.style);
  const segments = Array.isArray(block?.segments) && block.segments.length
    ? block.segments
    : [{ text: String(block?.text || ''), style: base, linkUrl: block?.linkUrl || '' }];
  const escapeText = (value) => String(value ?? '').replace(/\\/g, '\\\\').replace(/\[/g, '\\[');
  return segments.map((segment) => {
    const current = normalize({ ...base, ...(segment?.style || {}) });
    const stack = [];
    const open = (tag) => stack.push(tag);
    if (current.fontSize !== base.fontSize) open(`size=${current.fontSize}`);
    if (current.color !== base.color) open(`color=${current.color}`);
    if (current.fontWeight !== base.fontWeight) open(current.fontWeight === 'bold' ? 'b' : `weight=${current.fontWeight}`);
    if (current.fontStyle !== base.fontStyle) open(current.fontStyle === 'italic' ? 'i' : 'italic=off');
    if (current.textDecoration !== base.textDecoration) open(current.textDecoration === 'underline' ? 'u' : 'underline=off');
    const link = normalizedLink(segment?.linkUrl || '');
    if (link) open(`link=${link}`);
    const content = escapeText(segment?.text);
    return stack.map((tag) => `[${tag}]`).join('') + content + stack.slice().reverse().map((tag) => `[/${tag.split('=')[0]}]`).join('');
  }).join('');
}
